import { AlertTriangle } from 'lucide-react';
import { usd } from '../agreementFormat';
import RichView from './RichView';
import { PageTitle, Paper } from './Paper';
import { withHeads } from './scopeHeads';

export const ScopeHeading = ({ name, b, tid }) => (
  <p className="border-b border-[#E6E8EC] pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: b.accent }} data-testid={tid}>{name}</p>
);

// The services the client turned down: names only, no prices, at the end of the page.
export const Declined = ({ names, b, tid }) => (names?.length ? (
  <div className="mt-6" data-testid={tid}>
    <p className="mb-1 text-[9px] font-semibold uppercase tracking-[0.2em]" style={{ color: b.accent }}>Declined</p>
    {names.map((n, i) => <p key={i} className="border-b border-[#E6E8EC] py-2 text-[13px] text-[#5C6370] last:border-b-0" data-testid={`${tid}-${i}`}>{n}</p>)}
  </div>
) : null);

function Price({ it, tid }) {
  return (
    <span className="shrink-0 text-right">
      <span className="block font-exo text-[12.5px] font-semibold tabular-nums" data-testid={`${tid}-price`}>{usd(it.price_cents)}</span>
      {it.unit_price_cents != null && <span className="block text-[10.5px] tabular-nums text-[#5C6370]" data-testid={`${tid}-each`}>{it.qty} {it.unit} × {usd(it.unit_price_cents)}</span>}
    </span>
  );
}

function Item({ it, itemized, tid }) {
  return (
    <li className="flex items-start justify-between gap-4 border-b border-[#E6E8EC] py-2 last:border-b-0" data-testid={tid}>
      <div className="min-w-0 flex-1">
        {it.name && <p className="font-exo text-[12.5px] font-semibold text-[#111417]" data-testid={`${tid}-name`}>{it.name}</p>}
        {it.needs_wording
          ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-800" data-testid={`${tid}-needs-wording`}><AlertTriangle className="h-3 w-3" />Wording needed</span>
          : <RichView html={it.wording} className="text-[12.5px] leading-relaxed text-[#111417]" />}
      </div>
      {itemized && <Price it={it} tid={tid} />}
    </li>
  );
}

// A service: its title (unless hidden), its note, its lines.
function Service({ s, i, b, itemized }) {
  return (
    <section data-testid={`agreement-preview-scope-${i}`}>
      {s.name != null && (
        <div className="flex items-baseline justify-between gap-3 border-b-2 pb-1" style={{ borderColor: b.primary }}>
          <h3 className="font-exo text-sm font-bold" style={{ color: b.primary }}>
            {s.name}{s.optional && <span className="ml-2 text-[9px] font-semibold uppercase tracking-[0.16em]" style={{ color: b.accent }} data-testid={`agreement-preview-scope-${i}-optional`}>Optional</span>}
          </h3>
          {s.total_cents != null && <span className="font-exo text-sm font-bold tabular-nums" data-testid={`agreement-preview-scope-${i}-total`}>{usd(s.total_cents)}</span>}
        </div>
      )}
      {s.note && <RichView html={s.note} className="pt-2 text-xs italic leading-relaxed text-[#5C6370]" tid={`agreement-preview-scope-${i}-note`} />}
      <ul>{s.items.map((it, j) => <Item key={j} it={it} itemized={itemized} tid={`agreement-preview-scope-${i}-item-${j}`} />)}</ul>
    </section>
  );
}

// The scope of work: every line and what it is, in the client's words; grouped by service unless the titles are hidden
// (then one list, and the Optional services after it, each keeping its title). With client picks only the services the
// agreement includes; the declined ones by name at the end.
export default function ScopePage({ pg, doc, n, total }) {
  const b = doc.brand;
  const itemized = doc.detail === 'itemized';
  const grouped = doc.service_titles !== false;
  const rows = doc.services.map((s, i) => ({ s, i }));
  const base = grouped ? rows : rows.filter((r) => !r.s.optional);
  const extra = grouped ? [] : rows.filter((r) => r.s.optional);
  return (
    <Paper brand={b} n={n} total={total} tid="agreement-preview-scope">
      <PageTitle brand={b} eyebrow="Scope of work" title={pg.title || 'What’s included'} />
      <div className={grouped ? 'space-y-6' : ''} data-testid={grouped ? 'agreement-preview-scope-grouped' : 'agreement-preview-scope-list'}>
        {withHeads(doc, base, (r) => r.s).map(({ head, item: { s, i } }) => (
          <div key={s.key} className={grouped ? 'space-y-3' : ''}>
            {head != null && <ScopeHeading name={head} b={b} tid={`agreement-preview-scopehead-${i}`} />}
            <Service s={s} i={i} b={b} itemized={itemized} />
          </div>
        ))}
      </div>
      {extra.length > 0 && <div className="mt-6 space-y-6">{extra.map(({ s, i }) => <Service key={s.key} s={s} i={i} b={b} itemized={itemized} />)}</div>}
      <Declined names={doc.declined} b={b} tid="agreement-preview-scope-declined" />
    </Paper>
  );
}
